"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getApproveContractData = exports.normalizeIntegerAmount = exports.normalizeAmount = exports.grantAllowance = exports.isApprovalRequired = exports.getERC20Allowance = void 0;
const caip_1 = require("@shapeshiftoss/caip");
const web3_utils_1 = require("web3-utils");
const api_1 = require("../../../api");
const constants_1 = require("../../cow/utils/constants");
const erc20_abi_1 = require("../abi/erc20-abi");
const bignumber_1 = require("../bignumber");
const getERC20Allowance = async ({ erc20AllowanceAbi, web3, sellAssetErc20Address, ownerAddress, spenderAddress, }) => {
    const erc20Contract = new web3.eth.Contract(erc20AllowanceAbi, sellAssetErc20Address);
    return erc20Contract.methods.allowance(ownerAddress, spenderAddress).call();
};
exports.getERC20Allowance = getERC20Allowance;
const isApprovalRequired = async ({ adapter, receiveAddress, allowanceContract, sellAsset, sellAmountExcludeFeeCryptoBaseUnit, web3, erc20AllowanceAbi, }) => {
    try {
        if (sellAsset.assetId === adapter.getFeeAssetId()) {
            return false;
        }
        const ownerAddress = receiveAddress;
        const spenderAddress = allowanceContract;
        const { assetReference: sellAssetErc20Address } = (0, caip_1.fromAssetId)(sellAsset.assetId);
        const allowanceOnChain = await (0, exports.getERC20Allowance)({
            web3,
            erc20AllowanceAbi,
            ownerAddress,
            spenderAddress,
            sellAssetErc20Address,
        });
        if (!allowanceOnChain) {
            throw new api_1.SwapError(`[isApprovalRequired] - No allowance data`, {
                details: { allowanceContract, receiveAddress },
                code: api_1.SwapErrorTypes.RESPONSE_ERROR,
            });
        }
        if ((0, bignumber_1.bn)(allowanceOnChain).isZero())
            return true;
        const allowanceRequired = (0, bignumber_1.bnOrZero)(sellAmountExcludeFeeCryptoBaseUnit).minus(allowanceOnChain);
        return allowanceRequired.gt(0);
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[isApprovalRequired]', {
            cause: e,
            code: api_1.SwapErrorTypes.ALLOWANCE_REQUIRED_FAILED,
        });
    }
};
exports.isApprovalRequired = isApprovalRequired;
const grantAllowance = async ({ quote, wallet, adapter, erc20Abi, web3, }) => {
    var _a, _b, _c, _d, _e;
    try {
        const { assetReference: sellAssetErc20Address } = (0, caip_1.fromAssetId)(quote.sellAsset.assetId);
        const erc20Contract = new web3.eth.Contract(erc20Abi, sellAssetErc20Address);
        const approveTx = erc20Contract.methods
            .approve(quote.allowanceContract, quote.sellAmountBeforeFeesCryptoBaseUnit)
            .encodeABI();
        const { bip44Params } = quote;
        const { txToSign } = await adapter.buildSendTransaction({
            wallet,
            to: sellAssetErc20Address,
            bip44Params,
            value: '0',
            chainSpecific: {
                erc20ContractAddress: sellAssetErc20Address,
                gasPrice: (0, web3_utils_1.numberToHex)(((_b = (_a = quote.feeData) === null || _a === void 0 ? void 0 : _a.chainSpecific) === null || _b === void 0 ? void 0 : _b.gasPriceCryptoBaseUnit) || 0),
                gasLimit: (0, web3_utils_1.numberToHex)(((_d = (_c = quote.feeData) === null || _c === void 0 ? void 0 : _c.chainSpecific) === null || _d === void 0 ? void 0 : _d.estimatedGas) || 0),
            },
        });
        const grantAllowanceTxToSign = {
            ...txToSign,
            data: approveTx,
        };
        if (wallet.supportsOfflineSigning()) {
            const signedTx = await adapter.signTransaction({ txToSign: grantAllowanceTxToSign, wallet });
            const broadcastedTxId = await adapter.broadcastTransaction(signedTx);
            return broadcastedTxId;
        }
        else if (wallet.supportsBroadcast() && adapter.signAndBroadcastTransaction) {
            const broadcastedTxId = await ((_e = adapter.signAndBroadcastTransaction) === null || _e === void 0 ? void 0 : _e.call(adapter, {
                txToSign: grantAllowanceTxToSign,
                wallet,
            }));
            return broadcastedTxId;
        }
        else {
            throw new api_1.SwapError('[grantAllowance] - invalid HDWallet config', {
                code: api_1.SwapErrorTypes.SIGN_AND_BROADCAST_FAILED,
            });
        }
    }
    catch (e) {
        if (e instanceof api_1.SwapError)
            throw e;
        throw new api_1.SwapError('[grantAllowance]', {
            cause: e,
            code: api_1.SwapErrorTypes.GRANT_ALLOWANCE_FAILED,
        });
    }
};
exports.grantAllowance = grantAllowance;
/**
 * This function keeps 17 significant digits, so even if we try to trade 1 Billion of an
 * ETH or ERC20, we still keep 7 decimal places.
 * @param amount
 */
const normalizeAmount = (amount) => {
    return (0, bignumber_1.bnOrZero)(amount).toNumber().toLocaleString('fullwide', { useGrouping: false });
};
exports.normalizeAmount = normalizeAmount;
const normalizeIntegerAmount = (amount) => {
    return (0, bignumber_1.bnOrZero)(amount)
        .integerValue()
        .toNumber()
        .toLocaleString('fullwide', { useGrouping: false });
};
exports.normalizeIntegerAmount = normalizeIntegerAmount;
const getApproveContractData = ({ web3, spenderAddress, contractAddress, }) => {
    const contract = new web3.eth.Contract(erc20_abi_1.erc20Abi, contractAddress);
    return contract.methods.approve(spenderAddress, constants_1.MAX_ALLOWANCE).encodeABI();
};
exports.getApproveContractData = getApproveContractData;
